/**
 * 답변 속 표 하나를 엑셀(.xlsx) 파일로 받기.
 *
 * 화면에 그려진 <table> 에서 글자를 읽으므로 마크다운 기호(`**`, `|`)는 이미 빠져 있다.
 * 숫자·백분율 칸은 엑셀에서 계산되게 숫자로 넣고, 단위가 붙은 칸(5명·45분)과 앞자리 0(01·007)은 글자로 둔다.
 */
import { tableElementToRows } from './answerClipboard';
import { downloadBlobFile } from './downloadUtils';

export const isChatTableDownloadEnabled = () => (
  !['false', '0', 'off'].includes(String(process.env.REACT_APP_CHAT_TABLE_DOWNLOAD || '').trim().toLowerCase())
);

const NUMBER_RE = /^[-+−]?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?$/;
const LEADING_ZERO_RE = /^[-+−]?0\d/;
const MAX_SAFE_DIGITS = 15;
// 숫자 뒤 단위(5명·45분·1,200원)는 숫자로 넣고 표시 형식 `0"명"` 으로 화면 글자를 그대로 둔다 — 엑셀에서 합계가 된다.
const UNIT_RE = /^(.*?\d)(\s?)(%|명|개|원|점|회|번|분|초|시간|일|주|개월|년|배|쪽|문항|kg|g|mg|cm|mm|km|m|mL|L)$/;

/** 칸 글자 → 엑셀 칸 값. 숫자가 아니면 글자 그대로. */
export const toSheetCell = (text) => {
  const raw = String(text ?? '').trim();
  const unitMatch = raw.match(UNIT_RE);
  const body = unitMatch ? unitMatch[1] : raw;
  const unit = unitMatch ? unitMatch[3] : '';
  if (!NUMBER_RE.test(body) || LEADING_ZERO_RE.test(body)) return { v: raw, t: 's' };
  if (body.replace(/\D/g, '').length > MAX_SAFE_DIGITS) return { v: raw, t: 's' };
  const number = body.replace(/,/g, '').replace('−', '-');
  const percent = unit === '%';
  const value = Number(percent ? `${number}e-2` : number);
  if (!Number.isFinite(value)) return { v: raw, t: 's' };
  const decimals = (body.split('.')[1] || '').length;
  const base = `${body.includes(',') ? '#,##0' : '0'}${decimals ? `.${'0'.repeat(decimals)}` : ''}`;
  if (percent) return { v: value, t: 'n', z: `${base}%` };
  if (unit) return { v: value, t: 'n', z: `${base}"${unitMatch[2]}${unit}"` };
  return body.includes(',') ? { v: value, t: 'n', z: base } : { v: value, t: 'n' };
};

const HEADING_RE = /^H[1-6]$/;

// 문단 전체가 굵은 글자 하나(`**1학기 성적 분포**`)면 제목처럼 쓴 것으로 본다.
const isBoldTitleParagraph = (el) => el.tagName === 'P'
  && el.children.length === 1
  && /^(STRONG|B)$/.test(el.firstElementChild.tagName)
  && el.textContent.trim() === el.firstElementChild.textContent.trim();

const headingIn = (el) => {
  if (HEADING_RE.test(el.tagName) || isBoldTitleParagraph(el)) return el;
  const inner = el.querySelectorAll?.('h1, h2, h3, h4, h5, h6');
  return inner && inner.length ? inner[inner.length - 1] : null;
};

/** 표 바로 앞(같은 답변 안)에서 가장 가까운 제목 글자. 없으면 ''. */
export const findTableHeading = (tableEl) => {
  const root = tableEl?.closest?.('.enhanced-education-content');
  let node = tableEl;
  while (node && node !== root) {
    for (let sibling = node.previousElementSibling; sibling; sibling = sibling.previousElementSibling) {
      const heading = headingIn(sibling);
      if (heading) return heading.textContent.replace(/\s+/g, ' ').trim();
    }
    node = node.parentElement;
  }
  return '';
};

export const buildTableFilename = (heading) => {
  const base = Array.from(String(heading || ''))
    .filter((ch) => ch.charCodeAt(0) >= 32)
    .join('')
    .replace(/[\\/:*?"<>|[\]]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 40)
    .trim();
  return `${base || '표'}.xlsx`;
};

const sheetNameFor = (filename) => filename.replace(/\.xlsx$/, '').replace(/['\\/:*?[\]]/g, '').slice(0, 31) || '표';

// 엑셀 열 너비(글자 수): 한글은 영문 두 글자 폭.
const columnWidths = (rows) => {
  const widths = [];
  rows.forEach((row) => row.forEach((text, index) => {
    const width = Array.from(String(text)).reduce((sum, ch) => sum + (ch.charCodeAt(0) > 0x2e7f ? 2 : 1), 0);
    widths[index] = Math.max(widths[index] || 0, width);
  }));
  return widths.map((width) => ({ wch: Math.min(Math.max(width + 2, 6), 60) }));
};

/**
 * SheetJS 무료판은 글자 서식을 쓰지 않는다 — 만든 파일의 styles.xml 에 굵은 글꼴 하나를 더하고 머리글 칸이 그 서식을 쓰게 바꾼다.
 * 고치다 실패하면 서식 없는 원래 파일을 그대로 쓴다.
 */
export const boldHeaderRow = (XLSX, data) => {
  try {
    const zip = XLSX.CFB.read(new Uint8Array(data), { type: 'array' });
    const find = (suffix) => zip.FileIndex[zip.FullPaths.findIndex((name) => name.endsWith(suffix))];
    const styles = find('xl/styles.xml');
    const sheet = find('xl/worksheets/sheet1.xml');
    if (!styles || !sheet) return data;
    const decoder = new TextDecoder();
    const encoder = new TextEncoder();
    let stylesXml = decoder.decode(styles.content);
    const fontsMatch = stylesXml.match(/<fonts count="(\d+)">([\s\S]*?)<\/fonts>/);
    const xfsMatch = stylesXml.match(/<cellXfs count="(\d+)">/);
    if (!fontsMatch || !xfsMatch) return data;
    const fontId = Number(fontsMatch[1]);
    const xfId = Number(xfsMatch[1]);
    const baseFont = fontsMatch[2].match(/<font>([\s\S]*?)<\/font>/);
    const boldFont = `<font><b/>${baseFont ? baseFont[1] : ''}</font>`;
    stylesXml = stylesXml
      .replace(fontsMatch[0], `<fonts count="${fontId + 1}">${fontsMatch[2]}${boldFont}</fonts>`)
      .replace(/<cellXfs count="\d+">([\s\S]*?)<\/cellXfs>/, (_match, inner) => (
        `<cellXfs count="${xfId + 1}">${inner}<xf numFmtId="0" fontId="${fontId}" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs>`
      ));
    const sheetXml = decoder.decode(sheet.content)
      .replace(/<c r="([A-Z]+1)"(?![^>]*\ss=)/g, `<c r="$1" s="${xfId}"`);
    styles.content = encoder.encode(stylesXml);
    sheet.content = encoder.encode(sheetXml);
    return XLSX.CFB.write(zip, { fileType: 'zip', type: 'array', compression: true });
  } catch (error) {
    return data;
  }
};

/** 그려진 표를 시트 하나짜리 .xlsx 로 만든다. */
export const buildTableWorkbook = (XLSX, rows, filename) => {
  const sheet = XLSX.utils.aoa_to_sheet(rows.map((row, rowIndex) => row.map((text) => (
    rowIndex === 0 ? { v: text, t: 's' } : toSheetCell(text)
  ))));
  sheet['!cols'] = columnWidths(rows);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, sheetNameFor(filename));
  const data = XLSX.write(book, { bookType: 'xlsx', type: 'array', compression: true });
  return boldHeaderRow(XLSX, data);
};

export const downloadTableAsXlsx = async (tableEl) => {
  const rows = tableElementToRows(tableEl);
  if (!rows.length) throw new Error('empty_table');
  const XLSX = await import('xlsx');
  const filename = buildTableFilename(findTableHeading(tableEl));
  const data = buildTableWorkbook(XLSX, rows, filename);
  downloadBlobFile(new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
  return { rows: rows.length, cols: Math.max(...rows.map((row) => row.length)), filename };
};
